/* SpawnAnything — rotating 3D voxel previews inside menu cards (build window, item window, army window, collection).
   One shared WebGL canvas lies over the page (pointer-events: none) and paints every registered card element through
   a scissor rect, like the lab pages; it only renders while at least one registered element is on screen, so the
   battle pays nothing while the menus are closed. Models come from src/ui/models3d.js (or any Object3D).
   Usage: const pv = getPreview3D(); pv.add(el, () => unitObject('ritter', 0)); … pv.remove(el) / pv.removeWithin(root). */
import * as THREE from 'three';

let inst = null;
/** the shared preview renderer (created on first use) */
export function getPreview3D() { return inst || (inst = create()); }

function create() {
  const canvas = document.createElement('canvas');
  canvas.className = 'aw-pv3d'; canvas.setAttribute('aria-hidden', 'true');
  Object.assign(canvas.style, { position: 'fixed', inset: '0', width: '100%', height: '100%', pointerEvents: 'none', zIndex: '35' });
  document.body.appendChild(canvas);
  let renderer = null;
  const scene = new THREE.Scene();
  scene.add(new THREE.HemisphereLight(0xeef3ff, 0x40444f, 1.5));
  const sun = new THREE.DirectionalLight(0xfff0dc, 2.4); sun.position.set(3, 6, 4); scene.add(sun);
  const cam = new THREE.PerspectiveCamera(28, 1, 0.05, 500);
  const views = new Map(); // el -> view
  let raf = 0, last = 0;

  function ensureRenderer() {
    if (renderer) return renderer;
    renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true });
    renderer.setPixelRatio(Math.min(devicePixelRatio || 1, 3)); // native: sharp like the game
    renderer.setScissorTest(true); renderer.autoClear = false;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    return renderer;
  }
  function fit(v) {
    const box = new THREE.Box3().setFromObject(v.obj), size = new THREE.Vector3(), c = new THREE.Vector3();
    box.getSize(size); box.getCenter(c);
    v.obj.position.sub(new THREE.Vector3(c.x, box.min.y, c.z)); // feet on y = 0, centred
    v.size = size;
  }
  function build(v) {
    try { v.obj = v.make(); } catch (e) { console.warn('Vorschau fehlgeschlagen', e); v.obj = new THREE.Group(); }
    v.holder.add(v.obj); fit(v); v.built = true;
  }
  const onScreen = r => r.width > 2 && r.height > 2 && r.bottom > 0 && r.top < innerHeight && r.right > 0 && r.left < innerWidth;
  // an element inside a closed window / hidden parent has no client rects
  const visible = el => el.isConnected && el.getClientRects().length > 0;

  let painted = false, idle = 0;
  function frame(now) {
    raf = 0;
    if (!views.size) { if (painted) { renderer.setScissor(0, 0, innerWidth, innerHeight); renderer.clear(); painted = false; } return; }
    const dt = Math.min(0.05, (now - (last || now)) / 1000); last = now;
    const w = innerWidth, h = innerHeight;
    // nothing registered is on screen (menus closed): no GL work, look again a few times per second
    const any = [...views.values()].some(v => visible(v.el));
    if (!any) {
      if (painted) { renderer.setScissor(0, 0, w, h); renderer.clear(); painted = false; }
      idle = setTimeout(kick, 250); return;
    }
    const R = ensureRenderer();
    if (canvas.width !== Math.round(w * R.getPixelRatio()) || canvas.height !== Math.round(h * R.getPixelRatio())) R.setSize(w, h, false);
    R.setScissor(0, 0, w, h); R.setClearColor(0, 0); R.clear(); painted = true;
    let shown = 0, builds = 0;
    for (const v of views.values()) {
      if (!visible(v.el)) continue;
      const r = v.el.getBoundingClientRect(); if (!onScreen(r)) continue;
      // clip to the scrolling container (window body) so models do not paint over the window header
      const clip = v.clip ? v.clip.getBoundingClientRect() : { left: 0, top: 0, right: w, bottom: h };
      const x0 = Math.max(r.left, clip.left), x1 = Math.min(r.right, clip.right), y0 = Math.max(r.top, clip.top), y1 = Math.min(r.bottom, clip.bottom);
      if (x1 - x0 < 2 || y1 - y0 < 2) continue;
      if (!v.built) { if (builds++ > 2) { shown++; continue; } build(v); } // a few models per frame keep the menu smooth
      shown++;
      if (v.spin) v.rot += dt * v.spin;
      v.holder.rotation.y = v.rot;
      for (const o of views.values()) o.holder.visible = o === v;
      const s = v.size, span = Math.max(s.y, Math.hypot(s.x, s.z) * 0.8, 0.2), d = span * 2.3 + 0.3; // the turntable shows the diagonal too
      cam.aspect = r.width / r.height; cam.position.set(0, s.y * 0.55 + d * 0.3, d); cam.lookAt(0, s.y * 0.46, 0); cam.updateProjectionMatrix();
      R.setViewport(r.left, h - r.bottom, r.width, r.height); R.setScissor(x0, h - y1, x1 - x0, y1 - y0);
      R.render(scene, cam);
    }
    raf = requestAnimationFrame(frame);
  }
  const kick = () => { clearTimeout(idle); if (!raf) { last = 0; raf = requestAnimationFrame(frame); } };
  function drop(v) {
    scene.remove(v.holder);
    v.holder.traverse(o => { if (o.geometry) o.geometry.dispose(); });
    views.delete(v.el);
  }

  return {
    /** register a card element; make() returns an Object3D (built lazily when it first shows). opts: { spin, rot, clip } */
    add(el, make, { spin = 0.6, rot = -0.5, clip = null } = {}) {
      if (views.has(el)) drop(views.get(el));
      const holder = new THREE.Group(); scene.add(holder);
      views.set(el, { el, make, holder, obj: null, built: false, size: new THREE.Vector3(1, 1, 1), spin, rot, clip });
      kick();
    },
    /** rebuild a card's model (team colour changed, level changed …) */
    rebuild(el, make) { const v = views.get(el); if (!v) return; if (v.obj) { v.holder.remove(v.obj); v.obj.traverse(o => o.geometry?.dispose()); } if (make) v.make = make; v.built = false; kick(); },
    remove(el) { const v = views.get(el); if (v) drop(v); },
    /** drop every card inside root (a window being re-rendered) */
    removeWithin(root) { for (const v of [...views.values()]) if (root.contains(v.el)) drop(v); },
    /** call after a window opened (render loop sleeps when nothing changed) */
    wake: kick,
    /** paint right now (from a scroll handler): the cards moved this frame, a rAF later the models would lag behind */
    renderNow() { if (raf) { cancelAnimationFrame(raf); raf = 0; } clearTimeout(idle); frame(performance.now()); },
  };
}
